/**
 * The single TanStack `RouterHistory` instance that backs the whole app.
 *
 * Both the `browserHistory` facade (this module) and the `router` object that
 * `withRouter` injects operate on this one instance. `main.js` passes the very
 * same instance to `createRouter({history})`, so any `push`/`replace` here runs
 * the router's full `beforeLoad`/loader pipeline (the router subscribes to it).
 *
 * Keeping the facade methods reading a module-level instance (rather than a
 * router passed in later) avoids the import cycle with `client/history.ts`,
 * which imports `browserHistory` from here (via the `react-router` alias).
 */
import {createBrowserHistory} from '@tanstack/react-router';

// The import-free leaf, NOT '@albert-io/mandark/authentication': that barrel imports
// client/framework and the notifier, which would cycle back through here.
import {isLoggingOut} from '@albert-io/mandark/authentication/logoutState';

import {extractState, parseSerializedPath, serializePath, toShimLocation} from './path';
import type {
  InjectedRouter,
  LocationDescriptor,
  LocationState,
  RouteLeaveHook,
  ShimLocation
} from './types';

/** The app-wide history instance. */
export const routerHistory = createBrowserHistory();

type TanstackHistory = typeof routerHistory;

/**
 * Build the RR3 `router`/`browserHistory` API on top of a TanStack
 * `RouterHistory`. Called once, for the module-level `browserHistory` singleton;
 * `withRouter` spreads that singleton (it does not call this per render).
 */
export function createRouterApi(history: TanstackHistory): InjectedRouter {
  // RR3's `browserHistory` updated `getCurrentLocation()` SYNCHRONOUSLY on
  // push/replace, and a lot of app code reads the location on the very next line
  // after navigating (e.g. Dennis `createQuestionSet` pushes the id-less
  // "new question" URL, then immediately `INITIALIZE_STORE`s, whose
  // `getQuestionSetParams()` parses `getCurrentLocation().pathname` to decide
  // whether to build a NEW question or fetch an existing one). TanStack routes
  // push/replace through an async `tryNavigation`: when a route-leave blocker is
  // registered (`setRouteLeaveHook` below — the Dennis queue registers one for the
  // unsaved-changes prompt), it `await`s the blocker fn, deferring the real history
  // mutation (and `location` update) to a microtask. That made the synchronous read
  // see the STALE pre-navigation URL and, in the Dennis flow, drop the new question's
  // type (→ a typeless question → "Element type is invalid" crash in
  // QuestionTypeManager).
  //
  // Restore RR3 semantics with an optimistic location: set it synchronously when a
  // navigation starts so `getCurrentLocation()` reflects the destination (and its
  // `state`/`key`) immediately, then drop it on the next microtask — by then the real
  // history has caught up (an unblocked push notifies synchronously; a blocked-but-
  // allowed push commits in the microtask its awaited blocker scheduled, which runs
  // before ours). A per-navigation token guards the clear: when navigations overlap
  // (e.g. push-then-replace in one tick), an earlier navigation's microtask must not
  // wipe a later navigation's still-pending optimistic location.
  let optimisticLocation: ShimLocation | null = null;
  let navigationToken = 0;

  const getCurrentLocation = (): ShimLocation =>
    optimisticLocation ?? toShimLocation(history.location);

  /**
   * Serialize + run a navigation while exposing its destination synchronously via
   * `getCurrentLocation()`. The optimistic location is built from the descriptor so
   * it carries `state`/`key`, not just the path.
   */
  const navigate = (
    location: LocationDescriptor,
    state: LocationState | undefined,
    run: (path: string, state: LocationState | undefined) => void
  ): void => {
    // A logout leaves the document live but cookie-less; late 401s must not reroute the page
    // we are already leaving. Covers the shim's push/replace/pushState/replaceState only —
    // `Link` navigates via `routerHistory` directly, so a 401 handler written against the
    // TanStack router rather than this facade would bypass it.
    if (isLoggingOut()) {
      return;
    }
    const path = serializePath(location);
    const resolvedState = state ?? extractState(location);
    optimisticLocation = toShimLocation({
      ...parseSerializedPath(path),
      state: resolvedState ?? null
    });
    const token = ++navigationToken;
    run(path, resolvedState);
    queueMicrotask(() => {
      // Only the most recent navigation clears the optimistic location, so an
      // earlier (already-superseded) navigation cannot wipe a newer destination.
      if (token === navigationToken) {
        optimisticLocation = null;
      }
    });
  };

  // RR3 kept exactly ONE leave hook per route: re-registering replaced it, and
  // it was removed on unmount. TanStack's `history.block` instead STACKS
  // blockers, so we track the active blocker per route key and replace it.
  const leaveBlockers = new Map<unknown, () => void>();

  return {
    push(location: LocationDescriptor, state?: LocationState) {
      navigate(location, state, (path, s) => history.push(path, s ?? undefined));
    },
    replace(location: LocationDescriptor, state?: LocationState) {
      navigate(location, state, (path, s) => history.replace(path, s ?? undefined));
    },
    // Legacy RR3 signatures: `state` first, `path` second. RR3's pushState
    // accepted a location-descriptor OBJECT as well as a string (e.g.
    // ConfirmLeaveModal forwards the leave hook's `nextRoute`, which the shim
    // hands it as a ShimLocation object), so `navigate` serializes it the same way
    // push does — otherwise an object reaches TanStack's history and navigates to
    // garbage.
    pushState(state: LocationState, path: LocationDescriptor) {
      navigate(path, state, (p, s) => history.push(p, s ?? undefined));
    },
    replaceState(state: LocationState, path: LocationDescriptor) {
      navigate(path, state, (p, s) => history.replace(p, s ?? undefined));
    },
    go(n: number) {
      history.go(n);
    },
    goBack() {
      history.back();
    },
    goForward() {
      history.forward();
    },
    setRouteLeaveHook(route: unknown, hook: RouteLeaveHook) {
      // Replace any existing hook for this route (RR3 semantics).
      leaveBlockers.get(route)?.();
      const unblock = history.block({
        // RR3's `setRouteLeaveHook` ONLY intercepted in-app SPA navigations — it
        // never armed the browser's native `beforeunload` prompt. TanStack's
        // `history.block` defaults `enableBeforeUnload` to `true`, so a registered
        // blocker fires the native "leave site?" dialog on reload/close/external
        // nav even when its `blockerFn` would allow the navigation (and even on
        // pages whose hook always returns "allow", e.g. the slide-direction hook in
        // AssignmentSettingsTransitionV2). Opt out to restore RR3 semantics: the
        // few flows that genuinely want a native unload warning register their own
        // `beforeunload` listeners (DraftGuess, LockdownMode, ConfirmLeaveHandler,
        // TeacherEvaluationDraft).
        enableBeforeUnload: false,
        blockerFn: ({nextLocation}) => {
          const result = hook(toShimLocation(nextLocation));
          // TanStack: a truthy blockerFn result BLOCKS navigation.
          if (result === false) {
            return true;
          }
          if (typeof result === 'string') {
            // RR3 showed a confirm prompt; cancelling blocks.
            return !window.confirm(result);
          }
          return false;
        }
      });
      const remove = () => {
        unblock();
        if (leaveBlockers.get(route) === unblock) {
          leaveBlockers.delete(route);
        }
      };
      leaveBlockers.set(route, unblock);
      return remove;
    },
    createPath(location: LocationDescriptor) {
      return serializePath(location);
    },
    createHref(location: LocationDescriptor) {
      return history.createHref(serializePath(location));
    },
    getCurrentLocation,
    get location() {
      return getCurrentLocation();
    }
  };
}

/** RR3 `browserHistory` singleton. */
export const browserHistory = createRouterApi(routerHistory);
